import { Link } from "wouter";
import { useT, LanguageSelector } from "@/lib/i18n";
import AppStoreBadges from "./AppStoreBadges";
import footerLogo from "../assets/icons/outclose.svg";
import imgInstagram from "../assets/icons/instagram.svg";
import imgTiktok from "../assets/icons/tiktok.svg";

interface LandingFooterProps {
  /**
   * "large" — the full marketing footer (signup CTA, badges, socials, etc.).
   * "small" — just the legal disclaimer (links + gay-owned + company).
   */
  size?: "large" | "small";
  /** Attribution tag passed to the app-store badge clicks. Only used by "large". */
  appStoreSource?: string;
}

/**
 * The full marketing footer — signup CTA, app-store badges, socials, language
 * selector and legal links. Shared by the landing page and the signed-out
 * events index. Pass `size="small"` for just the legal disclaimer.
 */
export function LandingFooter({ size = "large", appStoreSource = "landing_footer" }: LandingFooterProps) {
  const { t } = useT("landing");

  if (size === "small") {
    return (
      <footer className="max-w-5xl mx-auto px-4 sm:px-6 mt-16 mb-8 text-center text-neutral-500 dark:text-neutral-400">
        <p className="text-sm">
          <Link href="/terms" className="hover:text-primary-500 transition-colors">
            {t("footer.terms")}
          </Link>
          {" | "}
          <Link href="/privacy" className="hover:text-primary-500 transition-colors">
            {t("footer.privacy")}
          </Link>
          {" | "}
          <Link href="/community-guidelines" className="hover:text-primary-500 transition-colors">
            {t("footer.communityGuidelines")}
          </Link>
          {" | "}
          <Link href="/help" className="hover:text-primary-500 transition-colors">
            {t("footer.help")}
          </Link>
        </p>
        <p className="mt-3 text-sm flex items-center justify-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded-sm bg-gradient-to-br from-red-500 via-yellow-400 to-violet-500" />
          {t("footer.proudlyGayOwned")}
        </p>
        <p className="mt-3 text-sm">
          <a href="https://outclose.com/company" className="hover:text-primary-500 transition-colors">
            GBH Holdings Inc.
          </a>
        </p>
      </footer>
    );
  }

  return (
    <div className="bg-white dark:bg-neutral-900 pb-16 md:mb-0">
      <div className="max-w-7xl mx-auto px-4 py-8 text-center">
        <p className="mb-8">
          <a href="https://app.outclose.com/" className="btn-primary mx-auto px-8 py-4 w-auto">
            {t("footer.signUpNow")}
          </a>
        </p>
        <AppStoreBadges className="justify-center mb-6" badgeHeight={36} source={appStoreSource} />
        <img src={footerLogo} alt="Logo" className="w-16 h-16 inline-block dark:invert" />
        <p className="text-2xl font-Poppins font-bold mb-2 dark:text-neutral-100">Outclose</p>
        <div className="flex justify-center items-center gap-2 mb-2">
          <a href="https://instagram.com/outclose.lgbt" target="_blank" rel="noreferrer">
            <img
              src={imgInstagram}
              alt="Instagram"
              className="inline-block ml-2 w-10 h-10 p-2 text-primary-500 dark:invert"
            />
          </a>
          <a href="https://www.tiktok.com/@outclose.lgbt" target="_blank" rel="noreferrer">
            <img
              src={imgTiktok}
              alt="TikTok"
              className="inline-block ml-2 w-10 h-10 p-2 text-primary-500 dark:invert"
            />
          </a>
        </div>
        <span className="dark:text-neutral-300">{t("footer.tagline")}</span>

        <div className="my-4 flex justify-center">
          <LanguageSelector className="dark:text-neutral-100 dark:border-neutral-600" />
        </div>
        <p className="my-4 text-sm text-center">
          <a
            href="#"
            className="cky-banner-element hover:text-primary-500"
            onClick={() => (window as any).revisitCkyConsent?.()}
          >
            {t("footer.manageCookies")}
          </a>
        </p>
        {/* Legal Links */}
        <p className="my-4 text-sm">
          <Link href="/terms" className="hover:text-primary-500 transition-colors">
            {t("footer.terms")}
          </Link>
          {" | "}
          <Link href="/privacy" className="hover:text-primary-500 transition-colors">
            {t("footer.privacy")}
          </Link>
          {" | "}
          <Link href="/community-guidelines" className="hover:text-primary-500 transition-colors">
            {t("footer.communityGuidelines")}
          </Link>
          {" | "}
          <Link href="/help" className="hover:text-primary-500 transition-colors">
            {t("footer.help")}
          </Link>
        </p>
        <p className="my-4 text-sm flex items-center justify-center gap-1.5 dark:text-neutral-400">
          <span className="inline-block w-2.5 h-2.5 rounded-sm bg-gradient-to-br from-red-500 via-yellow-400 to-violet-500" />
          {t("footer.proudlyGayOwned")}
        </p>
        <p className="my-4 text-sm dark:text-neutral-400">
          <a href="https://outclose.com/company" className="hover:text-primary-500 transition-colors">
            GBH Holdings Inc.
          </a>
        </p>
      </div>
    </div>
  );
}

export default LandingFooter;
